import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import classNames from 'classnames';
import Modelo3dCard from './Modelo3dCard.jsx';
import Modelo3dCardListAdmin from './Modelo3dCardListAdmin.jsx';
import DropdownOrder from '../../../Store/DropdownOrder.jsx';
import DropdownTipoVista from '../../../Store/DropdownTipoVista.jsx';
import BuscadorInput from '../../../Store/BuscadorInput.jsx';
import { Box, Calendar, Clock, Boxes } from 'lucide-react';

const SORT_OPTIONS = [
    { nombre: 'Nombre',             valor: 'name',        icon: <Box     className="w-4 h-4 mr-2" /> },
    { nombre: 'Fecha de creación',  valor: 'created_at',  icon: <Calendar className="w-4 h-4 mr-2" /> },
    { nombre: 'Última modificación',valor: 'modified_at', icon: <Clock   className="w-4 h-4 mr-2" /> },
];

const VIEW_OPTIONS = [
    { label: '2 columnas', value: { mode: 'grid', columns: 2 } },
    { label: '3 columnas', value: { mode: 'grid', columns: 3 } },
    { label: '4 columnas', value: { mode: 'grid', columns: 4 } },
    { label: 'Lista',      value: { mode: 'list' } },
];

const INITIAL_STATE = {
    mode: 'list',
    columns: 3,
    sortBy: 'created_at',
    sortDirection: 'desc',
};

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debouncedValue;
};

const ListaModelosLlaverosTodosAdmin = () => {
    const [modelos3d, setModelos3d] = useState([]);
    const [loading, setLoading]     = useState(false);
    const [hasMore, setHasMore]     = useState(true);
    const [totalCount, setTotalCount] = useState(null);
    const observer = useRef(null);
    const nextPage = useRef(2);
    const loadingRef = useRef(false);
    const requestGeneration = useRef(0);

    const [viewMode, setViewMode] = useState(() => {
        const saved = Cookies.get('viewModeModelosAdmin');
        return saved ? JSON.parse(saved) : INITIAL_STATE;
    });
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    const fetchModelos3d = useCallback((page = 1, reset = false, generation = requestGeneration.current) => {
        if (loadingRef.current && !reset) return;

        loadingRef.current = true;
        setLoading(true);
        axios.get('customized-keyrings-admin/', {
            params: {
                page,
                sortBy: viewMode.sortBy,
                sortDirection: viewMode.sortDirection,
                search: debouncedSearchTerm || undefined,
            },
        })
            .then(({ data }) => {
                if (generation !== requestGeneration.current) return;
                const results = Array.isArray(data) ? data : data.results;
                setModelos3d(prev => reset ? results : [...prev, ...results]);
                setHasMore(Array.isArray(data) ? false : Boolean(data.next));
                setTotalCount(Array.isArray(data) ? data.length : (data.count ?? null));
                nextPage.current = page + 1;
            })
            .catch(err => console.error('Error fetching modelos admin:', err))
            .finally(() => {
                if (generation !== requestGeneration.current) return;
                loadingRef.current = false;
                setLoading(false);
            });
    }, [viewMode.sortBy, viewMode.sortDirection, debouncedSearchTerm]);

    const lastItemRef = useCallback(node => {
        if (loading) return;
        observer.current?.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                fetchModelos3d(nextPage.current);
            }
        }, { rootMargin: '200px 0px' });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, fetchModelos3d]);

    useEffect(() => {
        requestGeneration.current += 1;
        loadingRef.current = false;
        setModelos3d([]);
        setHasMore(true);
        nextPage.current = 2;
        fetchModelos3d(1, true, requestGeneration.current);
    }, [fetchModelos3d]);

    useEffect(() => {
        Cookies.set('viewModeModelosAdmin', JSON.stringify(viewMode), { expires: 7 });
    }, [viewMode]);

    useEffect(() => () => observer.current?.disconnect(), []);

    const handleDelete = (modeloId) => {
        setModelos3d(prev => prev.filter(m => m.id !== modeloId));
        setTotalCount(prev => prev !== null ? prev - 1 : null);
    };

    const gridStyle = useMemo(() => viewMode.mode === 'grid'
        ? { gridTemplateColumns: `repeat(${viewMode.columns}, minmax(0, 1fr))` }
        : undefined, [viewMode.mode, viewMode.columns]);

    const initialLoading = loading && modelos3d.length === 0;

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">
            {/* Toolbar */}
            <div className="glassCard rounded-xl p-3 z-40 flex flex-wrap gap-2 items-center justify-between">
                <div className="flex items-center gap-2">
                    <Boxes className="w-5 h-5 text-primary" />
                    <span className="font-semibold text-sm">Todos los modelos</span>
                    {totalCount !== null && (
                        <span className="badge badge-ghost badge-sm">{totalCount}</span>
                    )}
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <DropdownOrder
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                        arrayDatosOrdenacion={SORT_OPTIONS}
                    />
                    <DropdownTipoVista
                        options={VIEW_OPTIONS}
                        selected={viewMode.mode}
                        selectedValue={viewMode}
                        onChange={val => setViewMode(prev => ({ ...prev, mode: val.mode, columns: val.columns }))}
                    />
                    <BuscadorInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                </div>
            </div>

            {/* Content */}
            <div className={classNames(
                'relative flex-1 min-h-0 overflow-y-auto scrollbar_mod glassCard rounded-xl',
                { 'flex items-center justify-center': initialLoading || (!loading && modelos3d.length === 0) }
            )}>
                {initialLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="loading loading-spinner loading-lg text-primary" />
                    </div>
                ) : modelos3d.length > 0 ? (
                    <div className="p-3">
                        <div
                            className={viewMode.mode === 'grid' ? 'grid gap-4' : 'flex flex-col gap-2'}
                            style={gridStyle}
                        >
                            {modelos3d.map((modelo, index) => {
                                const isLast = index === modelos3d.length - 1;
                                const CardComponent = viewMode.mode === 'grid' ? Modelo3dCard : Modelo3dCardListAdmin;
                                const card = (
                                    <CardComponent
                                        modelo={modelo}
                                        onDelete={handleDelete}
                                    />
                                );

                                return isLast
                                    ? <div ref={lastItemRef} key={modelo.id || index}>{card}</div>
                                    : <React.Fragment key={modelo.id || index}>{card}</React.Fragment>;
                            })}
                        </div>

                        {loading && (
                            <div className="flex items-center justify-center gap-2 py-5 text-base-content/60">
                                <span className="loading loading-spinner loading-sm" />
                                <span className="text-sm">Cargando más modelos...</span>
                            </div>
                        )}

                        {!hasMore && modelos3d.length > 0 && !loading && (
                            <p className="text-center text-xs text-base-content/40 py-4">
                                {modelos3d.length} modelos cargados
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-2 text-center">
                        <Boxes size={40} className="text-base-content/20" />
                        <p className="text-base-content/50">No hay modelos 3D disponibles.</p>
                        <p className="text-sm text-base-content/40">Los modelos creados por los clientes aparecerán aquí.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ListaModelosLlaverosTodosAdmin;
